import React, { useState } from 'react'
import { Dispatch } from 'redux'
import { connect } from 'react-redux'
import { Form, FastField, FieldArray, FormikProps } from 'formik'
import InputAdornment from '@material-ui/core/InputAdornment'
import {
  IDatafileFormInputs,
  DataStreamForPreviewType,
} from '../../types/datafile'
import { getDataFile } from '../../actions/datafile'
import {
  adaptDataForDataStream,
  defaultDatafileRequest,
} from '../../services/datafile'
import { DetailsTemplate, Space, SwitchTemplate } from '../../layouts'
import {
  CustomTextField,
  Button,
  H2,
  H3,
  P,
  DisabledUploadHint,
} from '../../components'
import { RequestForm } from './RequestForm'

export interface IDataFileForm {
  formikProps: FormikProps<IDatafileFormInputs>
  submitText?: string
  datastream?: DataStreamForPreviewType
  dataStreamError?: string
  isFormDisabled?: boolean
  packFormDirty?: boolean
  hideFileNameAndTitle?: boolean
  cancelAction: () => void
  dispatch: Dispatch
}

export const DataFileFormComponent = ({
  formikProps: { values, isValid },
  submitText = 'Save Data File',
  datastream,
  dataStreamError,
  isFormDisabled = false,
  packFormDirty = false,
  hideFileNameAndTitle = false,
  cancelAction,
  dispatch,
}: IDataFileForm) => {
  const [submitDisabled, setSubmitDisabled] = useState(false)
  return (
    <Form>
      <DetailsTemplate
        aside={
          <>
            <H2 mb={4}>Actions</H2>
            <DisabledUploadHint packFormDirty={packFormDirty} />
            <Button
              id="submit-datafile-form-data"
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={
                !isValid || submitDisabled || packFormDirty || isFormDisabled
              }
            >
              {submitText}
            </Button>
            <Space pt={4}>
              <Button id="cancel-button" fullWidth onClick={cancelAction}>
                Cancel
              </Button>
            </Space>
          </>
        }
        contentProps={{ id: 'datafile-create-tab-main' }}
        asideProps={{ id: 'datafile-create-tab-aside' }}
      >
        <SwitchTemplate
          buttons={[
            {
              label: 'Metadata',
              onClick: () => {
                setSubmitDisabled(!submitDisabled)
              },
              disabled: isFormDisabled,
            },
            {
              label: 'Preview',
              onClick: () => {
                dispatch(getDataFile(adaptDataForDataStream(values)))
                setSubmitDisabled(!submitDisabled)
              },
              disabled: isFormDisabled,
            },
          ]}
        >
          <div>
            {hideFileNameAndTitle ? null : (
              <>
                <FastField
                  name="fileName"
                  label="Filename"
                  placeholder="Enter filename"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">.json</InputAdornment>
                    ),
                  }}
                  component={CustomTextField}
                  disabled={isFormDisabled}
                />
                <FastField
                  name="title"
                  id="title"
                  label="Title"
                  placeholder="Enter title"
                  component={CustomTextField}
                  disabled={isFormDisabled}
                />
              </>
            )}
            <FieldArray
              name="requests"
              render={(arrayHelpers) => (
                <>
                  {values.requests?.map((request, i, arr) => (
                    // TODO: resolve: 'Warning: `value` prop on `input` should not be null.
                    // Consider using an empty string to clear the component or
                    // `undefined` for uncontrolled components' @Andrew
                    <RequestForm
                      key={i}
                      index={i}
                      {...request}
                      count={arr.length}
                      removeRequest={arrayHelpers.remove}
                      isFormDisabled={isFormDisabled}
                    />
                  ))}
                  <Space pt={8} pr={5}>
                    <Button
                      variant="outlined"
                      id="add-request"
                      type="button"
                      color="primary"
                      onClick={() => arrayHelpers.push(defaultDatafileRequest)}
                      fullWidth
                      disabled={isFormDisabled}
                    >
                      Add request
                    </Button>
                  </Space>
                </>
              )}
            />
          </div>
          <div>
            {dataStreamError && (
              <>
                <H3>The remote server returned an error: (400) Bad Request</H3>
                <P>{`Error Message: ${dataStreamError}`}</P>
              </>
            )}
            {datastream && <pre>{JSON.stringify(datastream, null, 2)}</pre>}
            {!datastream && !dataStreamError && <P>Loading data...</P>}
          </div>
        </SwitchTemplate>
      </DetailsTemplate>
    </Form>
  )
}

export const DataFileForm = connect()(DataFileFormComponent)
